// announcements.ts - Announcements service for CRUD and real-time subscriptions
import { supabase } from './supabase';
import type { Announcement, AnnouncementPriority } from '../types';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export interface AnnouncementTemplate {
  id: string;
  household_id: string | null;
  name: string;
  description: string | null;
  icon: string;
  title_template: string;
  message_template: string | null;
  priority: AnnouncementPriority;
  color: string | null;
  is_sticky: boolean;
  show_on_dashboard: boolean;
  show_on_screensaver: boolean;
  default_duration_hours: number | null;
  is_system: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateTemplateData {
  household_id: string;
  name: string;
  description?: string;
  icon?: string;
  title_template: string;
  message_template?: string;
  priority?: AnnouncementPriority;
  color?: string;
  is_sticky?: boolean;
  show_on_dashboard?: boolean;
  show_on_screensaver?: boolean;
  default_duration_hours?: number;
  created_by?: string;
}

export interface GetAnnouncementsOptions {
  showOnDashboard?: boolean;
  showOnScreensaver?: boolean;
  includeDismissedFor?: string; // User ID - if provided, filters out dismissed
  maxItems?: number;
}

export interface CreateAnnouncementData {
  household_id: string;
  title: string;
  message?: string;
  icon?: string;
  priority?: AnnouncementPriority;
  color?: string;
  created_by?: string;
  show_on_dashboard?: boolean;
  show_on_screensaver?: boolean;
  is_sticky?: boolean;
  starts_at?: string;
  expires_at?: string;
}

/**
 * Get active announcements for a household with proper filtering
 */
export async function getActiveAnnouncements(
  householdId: string,
  options: GetAnnouncementsOptions = {}
): Promise<Announcement[]> {
  const {
    showOnDashboard,
    showOnScreensaver,
    includeDismissedFor,
    maxItems = 10,
  } = options;

  const now = new Date().toISOString();

  let query = supabase
    .from('announcements')
    .select(`
      *,
      creator:family_members!created_by(id, name, nickname, avatar_emoji, avatar_url, color)
    `)
    .eq('household_id', householdId)
    .lte('starts_at', now) // Only show if started
    .or(`expires_at.is.null,expires_at.gt.${now}`) // Not expired
    .order('is_sticky', { ascending: false }) // Sticky first
    .order('priority', { ascending: false }) // Then by priority
    .order('created_at', { ascending: false }) // Then newest
    .limit(maxItems);

  // Filter by visibility flags
  if (showOnDashboard !== undefined) {
    query = query.eq('show_on_dashboard', showOnDashboard);
  }
  if (showOnScreensaver !== undefined) {
    query = query.eq('show_on_screensaver', showOnScreensaver);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching announcements:', error);
    return [];
  }

  let announcements = data as Announcement[];

  // Filter out dismissed announcements for this user
  if (includeDismissedFor) {
    announcements = announcements.filter(
      (a) => !a.dismissed_by?.includes(includeDismissedFor)
    );
  }

  return announcements;
}

/**
 * Dismiss an announcement for a specific user
 * Adds the user ID to the dismissed_by array
 */
export async function dismissAnnouncement(
  announcementId: string,
  userId: string
): Promise<boolean> {
  // First get the current dismissed_by array
  const { data: current, error: fetchError } = await supabase
    .from('announcements')
    .select('dismissed_by')
    .eq('id', announcementId)
    .single();

  if (fetchError) {
    console.error('Error fetching announcement for dismissal:', fetchError);
    return false;
  }

  const dismissedBy = current?.dismissed_by || [];

  // Don't add if already dismissed
  if (dismissedBy.includes(userId)) {
    return true;
  }

  // Add user to dismissed_by array
  const { error: updateError } = await supabase
    .from('announcements')
    .update({ dismissed_by: [...dismissedBy, userId] })
    .eq('id', announcementId);

  if (updateError) {
    console.error('Error dismissing announcement:', updateError);
    return false;
  }

  return true;
}

/**
 * Clear all dismissals for an announcement (called when announcement is updated)
 * This makes the announcement reappear for everyone
 */
export async function clearDismissals(announcementId: string): Promise<boolean> {
  const { error } = await supabase
    .from('announcements')
    .update({ dismissed_by: [] })
    .eq('id', announcementId);

  if (error) {
    console.error('Error clearing dismissals:', error);
    return false;
  }

  return true;
}

/**
 * Create a new announcement
 */
export async function createAnnouncement(
  data: CreateAnnouncementData
): Promise<Announcement | null> {
  const { data: result, error } = await supabase
    .from('announcements')
    .insert({
      ...data,
      icon: data.icon || '📢',
      priority: data.priority || 'normal',
      show_on_dashboard: data.show_on_dashboard ?? true,
      show_on_screensaver: data.show_on_screensaver ?? false,
      is_sticky: data.is_sticky ?? false,
      starts_at: data.starts_at || new Date().toISOString(),
      dismissed_by: [],
    })
    .select(`
      *,
      creator:family_members!created_by(id, name, nickname, avatar_emoji, avatar_url, color)
    `)
    .single();

  if (error) {
    console.error('Error creating announcement:', error);
    return null;
  }

  return result as Announcement;
}

/**
 * Update an existing announcement
 * Automatically clears dismissals so everyone sees the update
 */
export async function updateAnnouncement(
  announcementId: string,
  data: Partial<CreateAnnouncementData>
): Promise<Announcement | null> {
  const { data: result, error } = await supabase
    .from('announcements')
    .update({
      ...data,
      dismissed_by: [], // Clear dismissals on update
    })
    .eq('id', announcementId)
    .select(`
      *,
      creator:family_members!created_by(id, name, nickname, avatar_emoji, avatar_url, color)
    `)
    .single();

  if (error) {
    console.error('Error updating announcement:', error);
    return null;
  }

  return result as Announcement;
}

/**
 * Delete an announcement
 */
export async function deleteAnnouncement(announcementId: string): Promise<boolean> {
  const { error } = await supabase
    .from('announcements')
    .delete()
    .eq('id', announcementId);

  if (error) {
    console.error('Error deleting announcement:', error);
    return false;
  }

  return true;
}

/**
 * Subscribe to real-time announcement changes for a household
 * Returns an unsubscribe function
 */
export function subscribeToAnnouncements(
  householdId: string,
  onUpdate: () => void
): () => void {
  const channel = supabase
    .channel(`announcements-${householdId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'announcements',
        filter: `household_id=eq.${householdId}`,
      },
      onUpdate
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

// ═══════════════════════════════════════════════════════════════════
// TEMPLATE FUNCTIONS
// ═══════════════════════════════════════════════════════════════════

/**
 * Get all available templates (system + household-specific)
 */
export async function getAnnouncementTemplates(
  householdId: string
): Promise<AnnouncementTemplate[]> {
  // Fetch system templates and household templates separately to avoid .or() issues
  const [systemResult, householdResult] = await Promise.all([
    supabase
      .from('announcement_templates')
      .select('*')
      .eq('is_system', true)
      .order('name', { ascending: true }),
    supabase
      .from('announcement_templates')
      .select('*')
      .eq('household_id', householdId)
      .eq('is_system', false)
      .order('name', { ascending: true }),
  ]);

  if (systemResult.error) {
    console.error('Error fetching system templates:', systemResult.error);
  }
  if (householdResult.error) {
    console.error('Error fetching household templates:', householdResult.error);
  }

  // Combine results: system templates first, then household templates
  const systemTemplates = (systemResult.data || []) as AnnouncementTemplate[];
  const householdTemplates = (householdResult.data || []) as AnnouncementTemplate[];

  return [...systemTemplates, ...householdTemplates];
}

/**
 * Get a single template by ID
 */
export async function getAnnouncementTemplate(
  templateId: string
): Promise<AnnouncementTemplate | null> {
  const { data, error } = await supabase
    .from('announcement_templates')
    .select('*')
    .eq('id', templateId)
    .single();

  if (error) {
    console.error('Error fetching announcement template:', error);
    return null;
  }

  return data as AnnouncementTemplate;
}

/**
 * Create a new announcement template
 */
export async function createAnnouncementTemplate(
  data: CreateTemplateData
): Promise<AnnouncementTemplate | null> {
  const { data: result, error } = await supabase
    .from('announcement_templates')
    .insert({
      ...data,
      icon: data.icon || '📢',
      priority: data.priority || 'normal',
      is_sticky: data.is_sticky ?? false,
      show_on_dashboard: data.show_on_dashboard ?? true,
      show_on_screensaver: data.show_on_screensaver ?? false,
      is_system: false, // User-created templates are never system templates
    })
    .select('*')
    .single();

  if (error) {
    console.error('Error creating announcement template:', error);
    return null;
  }

  return result as AnnouncementTemplate;
}

/**
 * Update an existing template
 */
export async function updateAnnouncementTemplate(
  templateId: string,
  data: Partial<CreateTemplateData>
): Promise<AnnouncementTemplate | null> {
  const { data: result, error } = await supabase
    .from('announcement_templates')
    .update({
      ...data,
      updated_at: new Date().toISOString(),
    })
    .eq('id', templateId)
    .select('*')
    .single();

  if (error) {
    console.error('Error updating announcement template:', error);
    return null;
  }

  return result as AnnouncementTemplate;
}

/**
 * Delete an announcement template
 */
export async function deleteAnnouncementTemplate(
  templateId: string
): Promise<boolean> {
  const { error } = await supabase
    .from('announcement_templates')
    .delete()
    .eq('id', templateId)
    .eq('is_system', false); // Can't delete system templates

  if (error) {
    console.error('Error deleting announcement template:', error);
    return false;
  }

  return true;
}

/**
 * Create an announcement from a template
 */
export async function createAnnouncementFromTemplate(
  template: AnnouncementTemplate,
  householdId: string,
  overrides: Partial<CreateAnnouncementData> = {}
): Promise<Announcement | null> {
  // Calculate expiry if template has default duration
  let expiresAt: string | undefined;
  if (template.default_duration_hours) {
    const expiry = new Date();
    expiry.setHours(expiry.getHours() + template.default_duration_hours);
    expiresAt = expiry.toISOString();
  }

  return createAnnouncement({
    household_id: householdId,
    title: overrides.title || template.title_template,
    message: overrides.message || template.message_template || undefined,
    icon: overrides.icon || template.icon,
    priority: overrides.priority || template.priority,
    color: overrides.color || template.color || undefined,
    is_sticky: overrides.is_sticky ?? template.is_sticky,
    show_on_dashboard: overrides.show_on_dashboard ?? template.show_on_dashboard,
    show_on_screensaver: overrides.show_on_screensaver ?? template.show_on_screensaver,
    starts_at: overrides.starts_at || new Date().toISOString(),
    expires_at: overrides.expires_at || expiresAt,
    created_by: overrides.created_by,
  });
}
